import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorDetails, errorMessage } from '../lib/api';
import { Banner } from '../components/Banner';
import type { Diagnostic, ImportOutcome } from '../lib/tests';
import { TIMING, buildWritingTemplate } from '../lib/writing-template';

/**
 * A Writing paper, asked for as what it is: two prompts.
 *
 * Every other module needs the template — a Reading paper is forty questions,
 * an answer key and three passages, and no form could ask for that without
 * becoming the builder this platform deliberately does not have. Writing is
 * two boxes and a picture. Making a teacher write template syntax for that,
 * and match an image by filename afterwards, was ceremony for its own sake.
 *
 * The template is still what gets imported. It is assembled here rather than
 * typed, so the paper that arrives is the same shape as every other one.
 */

export function WritingPaperForm({
  onCancel,
  setName,
}: {
  onCancel: () => void;
  /** The whole test these two tasks belong to, chosen on the way in. */
  setName?: string;
}) {
  const navigate = useNavigate();
  const chart = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [taskOne, setTaskOne] = useState('');
  const [taskTwo, setTaskTwo] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<Diagnostic[] | null>(null);

  const ready = taskOne.trim().length > 20 && taskTwo.trim().length > 20;

  async function create() {
    setBusy(true);
    setError(null);
    setRejected(null);
    try {
      const template = buildWritingTemplate({
        title,
        taskOne,
        taskTwo,
        imageName: image?.name ?? null,
      });
      const { data } = await api.post<ImportOutcome>('/tests/import/text', {
        template,
        title: title.trim() || undefined,
        setName: setName?.trim() || undefined,
      });

      // The chart goes up with the paper rather than being matched to it by
      // name afterwards: the file is right here, and the test now exists.
      if (image) {
        const form = new FormData();
        form.append('file', image);
        await api.post(`/tests/${data.testId}/assets`, form);
      }
      navigate(`/tests/${data.testId}`);
    } catch (err) {
      setError(errorMessage(err, 'That could not be created'));
      setRejected(errorDetails<{ diagnostics?: Diagnostic[] }>(err)?.diagnostics ?? null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {error && <Banner tone="error">{error}</Banner>}
      {rejected && rejected.length > 0 && (
        <div className="panel">
          <ul className="stack">
            {rejected.map((d, i) => (
              <li key={i}>
                Line {d.line}: {d.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <input
        className="field__input"
        style={{ maxWidth: '28rem' }}
        placeholder="Paper title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <section className="panel stack">
        <div>
          <h2 className="panel__title">Task 1</h2>
          <p className="muted">{TIMING[1]}</p>
        </div>
        <textarea
          className="field__input writer__task"
          rows={6}
          placeholder="The graph below shows…&#10;Summarise the information by selecting and reporting the main features, and make comparisons where relevant."
          value={taskOne}
          onChange={(e) => setTaskOne(e.target.value)}
        />
        <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
          <button className="btn" type="button" onClick={() => chart.current?.click()}>
            {image ? 'Choose a different chart' : 'Add the chart or diagram'}
          </button>
          {image && <span className="muted">{image.name}</span>}
          {image && (
            <button className="btn btn--ghost" type="button" onClick={() => setImage(null)}>
              Remove
            </button>
          )}
          <input
            ref={chart}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              setImage(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
        </div>
      </section>

      <section className="panel stack">
        <div>
          <h2 className="panel__title">Task 2</h2>
          <p className="muted">{TIMING[2]}</p>
        </div>
        <textarea
          className="field__input writer__task"
          rows={8}
          placeholder="Some people believe…&#10;Discuss both these views and give your own opinion.&#10;Give reasons for your answer and include any relevant examples from your own knowledge or experience."
          value={taskTwo}
          onChange={(e) => setTaskTwo(e.target.value)}
        />
      </section>

      <div className="row" style={{ gap: 'var(--s-3)' }}>
        <button className="btn btn--primary" disabled={!ready || busy} onClick={() => void create()}>
          {busy ? 'Creating…' : 'Create the paper'}
        </button>
        <button className="btn btn--ghost" onClick={onCancel}>
          Write the template instead
        </button>
        {!ready && <span className="muted">Both tasks need their prompt.</span>}
      </div>
    </div>
  );
}
